import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  updateEntity,
  createEntity,
  deleteRelationship,
  apiFetch,
  api,
} from "../services/api";
import { getEntityTypes } from "../utils/entityTypes";
import { normalizeRelationshipStatus } from "../utils/relationshipStatus";
import { useEntityForm } from "./hooks/useEntityForm";
import { useRelationshipRules } from "../hooks/useRelationshipRules";
import EntityBasicInfo from "./EntityBasicInfo";
import EntityCustomAttributes from "./EntityCustomAttributes";
import EntityRelationships from "./EntityRelationships";
import EntityFormActions from "./EntityFormActions";
import RelationshipViewer from "./RelationshipViewer";
import GenerateDocumentButton from "./GenerateDocumentButton";
import EntityEnrichPanel from "./EntityEnrichPanel";
import EnrichReviewDialog, { parseJobResult } from "./EnrichReviewDialog";
import { createDevConsole } from "../utils/devLogger";
import { SECTION_CATALOG } from "./relationships/SectionCatalog";
import { normalizeEntityType } from "./relationships/SectionConfig";
import { parseEntityIdArray } from "../utils/entityIdArrays";

const console = createDevConsole();

/**
 * Convert frontend entity type names to database format
 */
const convertFrontendTypeToDatabase = (frontendName) => {
  const reverseMap = {
    Goal: "objective",
    Platform: "platform",
    Initiative: "initiative",
    Organization: "organization",
    BusinessContext: "business-context",
    ApplicationComponent: "application",
    ITComponent: "technology-component",
    Provider: "provider",
    ApplicationInterface: "interface",
    DataObject: "data-object",
    BusinessCapability: "business-capability",
    BusinessActor: "business-actor",
  };

  return reverseMap[frontendName] || frontendName;
};

const normalizeString = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const normalizeLowerString = (value) => normalizeString(value).toLowerCase();

const normalizeAliasNames = (value) => {
  const base = normalizeString(value);
  if (!base) return "";
  return base
    .split(/[,;]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(";")
    .toLowerCase();
};

const normalizeStatusValue = (value) =>
  normalizeLowerString(value) || "processed";

const normalizeIdArray = (value) => {
  const ids = parseEntityIdArray(value).map((val) => normalizeString(val)).filter(Boolean);
  return Array.from(new Set(ids)).sort();
};

const sortObjectKeys = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => sortObjectKeys(item));
  }
  if (value && typeof value === "object") {
    return Object.keys(value)
      .sort()
      .reduce((acc, key) => {
        acc[key] = sortObjectKeys(value[key]);
        return acc;
      }, {});
  }
  return value;
};

const normalizeCustomAttributes = (value) => {
  if (!value) return "{}";
  let parsed = value;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = {};
    }
  }
  return JSON.stringify(sortObjectKeys(parsed || {}));
};

const collectStandardFieldMismatches = (formState, persistedEntity) => {
  const comparisons = [
    {
      field: "name",
      form: normalizeString(formState.name),
      persisted: normalizeString(persistedEntity.name),
    },
    {
      field: "type",
      form: normalizeLowerString(formState.type),
      persisted: normalizeLowerString(
        persistedEntity.type || persistedEntity.entity_type
      ),
    },
    {
      field: "subtype",
      form: normalizeLowerString(formState.subtype),
      persisted: normalizeLowerString(persistedEntity.subtype),
    },
    {
      field: "status",
      form: normalizeStatusValue(
        formState.status || formState.processing_status
      ),
      persisted: normalizeStatusValue(
        persistedEntity.processing_status || persistedEntity.status
      ),
    },
    {
      field: "description",
      form: normalizeString(formState.description),
      persisted: normalizeString(persistedEntity.description),
    },
    {
      field: "alias_names",
      form: normalizeAliasNames(formState.alias_names),
      persisted: normalizeAliasNames(persistedEntity.alias_names),
    },
    {
      field: "parent",
      form: normalizeString(formState.parent),
      persisted: normalizeString(persistedEntity.parent),
    },
    {
      field: "children",
      form: normalizeIdArray(formState.children).join(","),
      persisted: normalizeIdArray(persistedEntity.children).join(","),
    },
    {
      field: "predecessors",
      form: normalizeIdArray(formState.predecessors).join(","),
      persisted: normalizeIdArray(persistedEntity.predecessors).join(","),
    },
    {
      field: "successors",
      form: normalizeIdArray(formState.successors).join(","),
      persisted: normalizeIdArray(persistedEntity.successors).join(","),
    },
    {
      field: "custom_attributes",
      form: normalizeCustomAttributes(formState.custom_attributes),
      persisted: normalizeCustomAttributes(persistedEntity.custom_attributes),
    },
  ];

  return comparisons
    .filter(({ form, persisted }) => form !== persisted)
    .map(({ field, form, persisted }) => ({
      field,
      expected: form,
      actual: persisted,
    }));
};

const EntityForm = ({
  editingEntity,
  /** Pre-select type when creating (no entity id). */
  defaultEntityType = "",
  onSave,
  onCancel,
  entities = [],
  allEntities = {},
  relationships = [],
  /** When true, relationship rows are still being fetched by the parent. */
  relationshipsLoading: relationshipsLoadingProp = false,
  readOnly = false,
  embedded = false,
  deferRelationshipMount = false,
  /** When true, only relationship sections + save actions (for inline edit from Read view). */
  relationshipsOnly = false,
  /** With relationshipsOnly: render only this SECTION_CATALOG section key. */
  onlyRelationshipSectionKey = null,
  /** Skip expensive post-save GET entity (inline Read uses a lighter parent refresh). */
  skipPostSaveEntityReload = false,
}) => {
  const entityId = String(editingEntity?.id ?? "").trim();
  const isCreateMode = !entityId;
  const [showAdvancedDetails, setShowAdvancedDetails] = useState(false);
  const [advancedSectionsMounted, setAdvancedSectionsMounted] = useState(
    () => Boolean(entityId)
  );
  const [activeTab, setActiveTab] = useState("form");
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [saveProgress, setSaveProgress] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [sourceModal, setSourceModal] = useState({
    open: false,
    loading: false,
    data: null,
  });
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    message: "",
    detail: null,
    onConfirm: null,
    onCancel: null,
  });
  /** Set when opening the entity type-change confirmation; Proceed reads this (avoids stale closures). */
  const entityTypeChangeRetryRef = useRef(null);
  const [entityTypesOptions, setEntityTypesOptions] = useState([]);
  const [enrichModalOpen, setEnrichModalOpen] = useState(false);
  const [enrichJobId, setEnrichJobId] = useState(null);
  /** Job id for a completed enrichment run (kept for persisting review state). */
  const [enrichReviewJobId, setEnrichReviewJobId] = useState(null);
  const [enrichPreview, setEnrichPreview] = useState(null);
  const [enrichReady, setEnrichReady] = useState(false);
  const [enrichReviewOpen, setEnrichReviewOpen] = useState(false);
  const [enrichAppliedRelationships, setEnrichAppliedRelationships] = useState([]);
  const [bootstrappedRelationships, setBootstrappedRelationships] =
    useState(null);
  const [bootstrappingRelationships, setBootstrappingRelationships] =
    useState(false);

  const relationshipsFromParent = useMemo(() => {
    const fromProps = Array.isArray(relationships) ? relationships : [];
    const fromEntity = Array.isArray(editingEntity?.relationships)
      ? editingEntity.relationships
      : [];
    return fromProps.length > 0 ? fromProps : fromEntity;
  }, [relationships, editingEntity?.relationships]);

  useEffect(() => {
    if (!entityId) {
      setBootstrappedRelationships(null);
      setBootstrappingRelationships(false);
      return undefined;
    }
    if (relationshipsFromParent.length > 0 || relationshipsLoadingProp) {
      setBootstrappedRelationships(null);
      setBootstrappingRelationships(false);
      return undefined;
    }

    let cancelled = false;
    setBootstrappingRelationships(true);
    api
      .getEntity(entityId, {
        includeRelationships: true,
        includeRelatedEntities: true,
        includeUnapproved: true,
      })
      .then((full) => {
        if (cancelled) return;
        setBootstrappedRelationships(
          Array.isArray(full?.relationships) ? full.relationships : []
        );
      })
      .catch(() => {
        if (!cancelled) setBootstrappedRelationships([]);
      })
      .finally(() => {
        if (!cancelled) setBootstrappingRelationships(false);
      });

    return () => {
      cancelled = true;
    };
  }, [entityId, relationshipsFromParent.length, relationshipsLoadingProp]);

  const effectiveRelationships = useMemo(() => {
    if (relationshipsFromParent.length > 0) return relationshipsFromParent;
    if (Array.isArray(bootstrappedRelationships)) {
      return bootstrappedRelationships;
    }
    return [];
  }, [relationshipsFromParent, bootstrappedRelationships]);

  const relationshipsLoading =
    relationshipsLoadingProp || bootstrappingRelationships;

  const [deferMountReady, setDeferMountReady] = useState(!deferRelationshipMount);

  useEffect(() => {
    if (!deferRelationshipMount) {
      setDeferMountReady(true);
      return undefined;
    }
    setDeferMountReady(false);
    const timer = setTimeout(() => setDeferMountReady(true), 150);
    return () => clearTimeout(timer);
  }, [deferRelationshipMount, editingEntity?.id]);

  const relationshipsReady =
    isCreateMode ||
    (deferMountReady &&
      !relationshipsLoading &&
      (!entityId ||
        relationshipsFromParent.length > 0 ||
        bootstrappedRelationships !== null));

  const mergedRelationships = useMemo(() => {
    const base = Array.isArray(effectiveRelationships)
      ? effectiveRelationships
      : [];
    const extra = Array.isArray(enrichAppliedRelationships)
      ? enrichAppliedRelationships
      : [];
    if (!extra.length) return base;
    const seen = new Set(base.map((r) => String(r?.id || "")));
    return [...base, ...extra.filter((r) => !seen.has(String(r?.id || "")))];
  }, [effectiveRelationships, enrichAppliedRelationships]);

  const {
    entityForm,
    setEntityForm,
    availableSubtypes,
    loadingSubtypes,
    loadingAllSubtypes,
    removedItems,
    setRemovedItems,
    fetchSubtypes,
  } = useEntityForm(editingEntity, entities, mergedRelationships, {
    defaultEntityType,
  });

  useEffect(() => {
    setEnrichAppliedRelationships([]);
    setEnrichJobId(null);
    setEnrichReviewJobId(null);
    setEnrichPreview(null);
    setEnrichReady(false);
    setEnrichReviewOpen(false);
  }, [editingEntity?.id]);

  const handleRelationshipsAppliedFromEnrich = useCallback((createdRels) => {
    if (!Array.isArray(createdRels) || createdRels.length === 0) return;
    setEnrichAppliedRelationships((prev) => {
      const current = Array.isArray(prev) ? prev : [];
      const seen = new Set(current.map((r) => String(r?.id || "")));
      const toAdd = createdRels.filter((r) => !seen.has(String(r?.id || "")));
      return toAdd.length ? [...current, ...toAdd] : current;
    });
  }, []);

  const handleDescriptionAppliedFromEnrich = useCallback(
    (text) => {
      setEntityForm((prev) => ({
        ...prev,
        description: text || "",
      }));
    },
    [setEntityForm]
  );

  const handleEnrichPreviewPatch = useCallback((mutator) => {
    setEnrichPreview((prev) => {
      if (!prev) return prev;
      return mutator(prev);
    });
  }, []);

  const handleEnrichmentQueued = useCallback((jobId) => {
    if (jobId) {
      setEnrichJobId(String(jobId));
    }
    setEnrichModalOpen(false);
  }, []);

  useEffect(() => {
    if (!enrichJobId) {
      return undefined;
    }
    let cancelled = false;
    const poll = async () => {
      try {
        const job = await api.getJobStatus(enrichJobId);
        if (cancelled || !job) return;
        const st = String(job.status || "").toLowerCase();
        if (st === "completed") {
          const preview = parseJobResult(job);
          setEnrichReviewJobId(String(enrichJobId));
          setEnrichPreview(preview || null);
          setEnrichReady(!!preview);
          setEnrichJobId(null);
          return;
        }
        if (st === "failed" || st === "cancelled") {
          setEnrichJobId(null);
        }
      } catch (e) {
        console.warn("Enrich job poll failed:", e);
      }
    };
    poll();
    const interval = setInterval(poll, 2500);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [enrichJobId]);

  // Use dynamic relationship rules from backend
  const {
    getRulesForEntityType,
    loading: rulesLoading,
    error: rulesError,
  } = useRelationshipRules();

  useEffect(() => {
    const fetchEntityTypes = async () => {
      try {
        const types = await getEntityTypes();
        setEntityTypesOptions(types);
      } catch (error) {
        console.error("Error fetching entity types:", error);
        // Fallback is handled in the utility function
        setEntityTypesOptions([]);
      }
    };
    fetchEntityTypes();
  }, []);

  // Helpers for viewing/downloading source description
  const downloadBlob = (filename, blob) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Failed to download file", e);
    }
  };

  const handleViewSource = async () => {
    try {
      setSourceModal({ open: true, loading: true, data: null });

      let extractionReason = editingEntity?.extraction_reason || null;
      if (!extractionReason && editingEntity?.id) {
        try {
          const full = await api.getEntity(editingEntity.id);
          extractionReason = full?.extraction_reason || null;
        } catch (e) {
          console.warn("Failed to fetch full entity for extraction reason", e);
        }
      }

      if (!extractionReason) {
        setSourceModal({ open: false, loading: false, data: null });
        alert("No extraction reason is recorded for this entity.");
        return;
      }

      setSourceModal({
        open: true,
        loading: false,
        data: {
          extraction_reason: extractionReason,
          text: extractionReason,
        },
      });
    } catch (e) {
      console.error("Failed to load extraction reason", e);
      setSourceModal({ open: false, loading: false, data: null });
      alert("Failed to load extraction reason.");
    }
  };

  // Populate relationship sections based on entity type (memoized)
  const populatedSections = useMemo(() => {
    if (!relationshipsReady) return {};
    const catalogType = normalizeEntityType(
      editingEntity?.entity_type || editingEntity?.type || entityForm.type || ""
    );
    // For catalog-backed types, EntityRelationships uses SECTION_CATALOG directly.
    // Skip legacy section synthesis entirely to avoid expensive upfront work.
    if (catalogType && SECTION_CATALOG[catalogType]) return {};

    const rules =
      getRulesForEntityType(
        editingEntity?.entity_type || editingEntity?.type || entityForm.type || ""
      ) || [];

    // Get available entities for each relationship type
    const availableEntitiesByType = {};
    entities.forEach((entity) => {
      // Entities from API have both 'type' and 'entity_type'
      const entityType = entity.type || entity.entity_type;
      if (!entityType) return;

      if (!availableEntitiesByType[entityType]) {
        availableEntitiesByType[entityType] = [];
      }
      availableEntitiesByType[entityType].push(entity);
    });
    console.log(
      "🔍 Available entities by type:",
      Object.keys(availableEntitiesByType),
      availableEntitiesByType
    );

    // Create a mapping of relationship sections to their available entities
    const relationshipSections = {};

    rules.forEach((rule) => {
      const relatedType = rule.relatedEntityType;

      // Convert PascalCase frontend types to database format for entity lookup
      const dbRelatedType = convertFrontendTypeToDatabase(relatedType);
      let availableEntities = availableEntitiesByType[dbRelatedType] || [];

      // Also check the PascalCase version
      if (availableEntities.length === 0) {
        availableEntities = availableEntitiesByType[relatedType] || [];
      }

      // For interface sections, explicitly filter to only include interface entities
      if (
        relatedType === "ApplicationInterface" ||
        relatedType === "interface" ||
        dbRelatedType === "interface"
      ) {
        // Filter to only include entities that are actually interfaces
        availableEntities = availableEntities.filter((entity) => {
          const entityType = entity.type || entity.entity_type || "";
          const typeLower = entityType.toLowerCase();
          return (
            typeLower === "interface" ||
            typeLower === "applicationinterface" ||
            typeLower.includes("interface")
          );
        });
      }

      console.log(
        "🔍 Available entities for type",
        relatedType,
        "(db:",
        dbRelatedType,
        "):",
        availableEntities.length
      );

      // For business-context, also include entities with business-context subtypes
      if (relatedType === "business-context") {
        const businessProcessEntities =
          availableEntitiesByType["BusinessProcess"] || [];
        const valueChainEntities = availableEntitiesByType["ValueChain"] || [];
        availableEntities = [
          ...availableEntities,
          ...businessProcessEntities,
          ...valueChainEntities,
        ];
      }

      // For parent relationships, include entities with subtypes of the same base type
      if (rule.key && rule.key.includes("_parent")) {
        // For business-context parents, include BusinessProcess and ValueChain
        if (relatedType === "business-context") {
          const businessProcessEntities =
            availableEntitiesByType["BusinessProcess"] || [];
          const valueChainEntities =
            availableEntitiesByType["ValueChain"] || [];
          availableEntities = [
            ...availableEntities,
            ...businessProcessEntities,
            ...valueChainEntities,
          ];
        }
        // For other entity types, just use the base type (no subtypes exist for them)
      }

      // For successor relationships, include both application and technology-component
      // But for application entities, only include application successors
      // And for technology-component entities, only include technology-component successors
      if (rule.key && rule.key.includes("_successor")) {
        if (entityForm.type === "application") {
          availableEntities = availableEntitiesByType["application"] || [];
        } else if (entityForm.type === "technology-component") {
          availableEntities =
            availableEntitiesByType["technology-component"] || [];
        } else {
          const appEntities = availableEntitiesByType["application"] || [];
          const techEntities =
            availableEntitiesByType["technology-component"] || [];
          availableEntities = [...appEntities, ...techEntities];
        }
      }

      // Map to the appropriate section key
      let sectionKey;

      // Special handling for parent relationships - use entity-specific parent section
      if (rule.key && rule.key.includes("_parent")) {
        sectionKey = `related_${relatedType.toLowerCase()}_parents`;
      }
      // Special handling for successor relationships - use a generic successor section
      else if (rule.key && rule.key.includes("_successor")) {
        sectionKey = "related_successors";
      }
      // Special handling for interface rules - use rule key for specific sections
      else if (rule.key === "interface_provided_by_app") {
        sectionKey = "interface_provided_by_app";
      } else if (rule.key === "interface_consumed_by_app") {
        sectionKey = "interface_consumed_by_app";
      } else if (rule.key === "interface_transfers_data") {
        sectionKey = "interface_transfers_data";
      } else if (rule.key === "provided_interfaces") {
        sectionKey = "provided_interfaces";
      } else if (rule.key === "consumed_interfaces") {
        sectionKey = "consumed_interfaces";
      }

      // Generic mapping - ONLY for outgoing relationships
      // For incoming relationships, let specific handlers below manage them
      if (!sectionKey && !rule.isIncoming) {
        if (
          relatedType === "business-capability" ||
          relatedType === "BusinessCapability"
        ) {
          sectionKey = "related_business_capabilities";
        } else if (
          relatedType === "business-context" ||
          relatedType === "BusinessContext"
        ) {
          sectionKey = "related_business_contexts";
        } else if (
          relatedType === "data-object" ||
          relatedType === "DataObject"
        ) {
          sectionKey = "related_data_objects";
        } else if (
          relatedType === "technology-component" ||
          relatedType === "ITComponent"
        ) {
          sectionKey = "related_it_components";
        } else if (
          relatedType === "initiative" ||
          relatedType === "Initiative"
        ) {
          sectionKey = "related_initiatives";
        } else if (
          relatedType === "application" ||
          relatedType === "ApplicationComponent"
        ) {
          sectionKey = "related_applications";
        } else if (
          relatedType === "business-actor" ||
          relatedType === "BusinessActor"
        ) {
          sectionKey = "related_business_actors";
        } else if (
          relatedType === "organization" ||
          relatedType === "Organization"
        ) {
          sectionKey = "related_organizations";
        } else if (relatedType === "provider" || relatedType === "Provider") {
          sectionKey = "related_providers";
        } else if (relatedType === "objective" || relatedType === "Goal") {
          sectionKey = "related_goals";
        } else if (
          relatedType === "ApplicationInterface" ||
          relatedType === "interface"
        ) {
          // Interfaces need special handling based on rule key
          if (rule.key.includes("provides")) {
            sectionKey = "provided_interfaces";
          } else if (rule.key.includes("consumes")) {
            sectionKey = "consumed_interfaces";
          }
        }
      }

      // Handle incoming relationship sections for interfaces
      // Check for interface entity type (case-insensitive, handle variations)
      const entityTypeLower = (entityForm.type || "").toLowerCase();
      const isInterfaceType =
        entityTypeLower === "interface" ||
        entityTypeLower === "applicationinterface" ||
        entityTypeLower === "application-interface" ||
        entityForm.type === "ApplicationInterface";

      if (rule.isIncoming && isInterfaceType) {
        if (
          rule.relationshipType === "provides" &&
          (relatedType === "ApplicationComponent" ||
            relatedType === "application" ||
            relatedType === "Application")
        ) {
          sectionKey = "interface_provided_by_app";
        } else if (
          rule.relationshipType === "consumes" &&
          (relatedType === "ApplicationComponent" ||
            relatedType === "application" ||
            relatedType === "Application")
        ) {
          sectionKey = "interface_consumed_by_app";
        } else if (
          rule.relationshipType === "affects" &&
          (relatedType === "Initiative" || relatedType === "initiative")
        ) {
          sectionKey = "interface_affected_by_initiative";
        }
      }

      // Handle incoming relationship sections for data objects
      if (rule.isIncoming && entityForm.type === "data-object") {
        if (
          rule.relationshipType === "transfers" &&
          relatedType === "Interface"
        ) {
          sectionKey = "data_object_transferred_by_interface";
        } else if (
          rule.relationshipType === "affects" &&
          (relatedType === "Initiative" || relatedType === "initiative")
        ) {
          sectionKey = "data_object_affected_by_initiative";
        }
      }

      // Handle incoming relationship sections for technology components
      if (rule.isIncoming && entityForm.type === "technology-component") {
        if (
          rule.relationshipType === "affects" &&
          (relatedType === "Initiative" || relatedType === "initiative")
        ) {
          sectionKey = "initiative_affects_it_reverse";
        } else if (
          rule.relationshipType === "provides" &&
          (relatedType === "Provider" || relatedType === "provider")
        ) {
          sectionKey = "it_provided_by_provider_reverse";
        }
      }

      // Handle incoming relationship sections for Application entity type
      if (
        rule.isIncoming &&
        (entityForm.type === "application" ||
          entityForm.type === "ApplicationComponent")
      ) {
        // Organizations that use this application
        if (
          rule.relationshipType === "uses" &&
          (relatedType === "Organization" || relatedType === "organization")
        ) {
          sectionKey = "organization_uses_application_reverse";
        }
      }

      // Handle incoming relationship sections for Objective entity type
      if (
        rule.isIncoming &&
        (entityForm.type === "objective" || entityForm.type === "Goal")
      ) {
        if (
          rule.relationshipType === "supports" &&
          (relatedType === "Initiative" || relatedType === "initiative")
        ) {
          sectionKey = "objective_supported_by_initiative";
        } else if (
          rule.relationshipType === "supports" &&
          (relatedType === "Platform" || relatedType === "platform")
        ) {
          sectionKey = "objective_supported_by_platform";
        }
      }

      // Handle incoming relationship sections for BusinessCapability entity type
      if (
        rule.isIncoming &&
        (entityForm.type === "business-capability" ||
          entityForm.type === "BusinessCapability")
      ) {
        // Applications supporting this capability
        if (
          (rule.relationshipType === "Enables/Supports" ||
            rule.relationshipType === "supports" ||
            rule.relationshipType === "enables") &&
          (relatedType === "ApplicationComponent" ||
            relatedType === "application")
        ) {
          sectionKey = "capability_supported_by_app";
        }
        // Business contexts associated/linked with this capability
        if (
          (rule.relationshipType === "Is Performed In" ||
            rule.relationshipType === "Associated With" ||
            rule.relationshipType === "Is Linked To") &&
          (relatedType === "BusinessContext" ||
            relatedType === "business-context")
        ) {
          sectionKey = "capability_performed_in_context";
        }
        // Goals/Initiatives improving/affecting this capability
        if (
          (rule.relationshipType === "Improves" ||
            rule.relationshipType === "Affects" ||
            rule.relationshipType === "Is Affected By") &&
          (relatedType === "Initiative" ||
            relatedType === "initiative" ||
            relatedType === "Goal" ||
            relatedType === "objective")
        ) {
          sectionKey = "capability_affected_by_initiative";
        }
      }

      // Handle incoming relationship sections for other entity types (if not handled above, use rule key as-is)
      if (rule.isIncoming && !sectionKey) {
        // The reverse rule key is already "original_rule_key_reverse", so just use it directly
        sectionKey = rule.key;
      }

      if (sectionKey) {
        // Use the available entities from the rule
        const entitiesToShow = availableEntities;

        relationshipSections[sectionKey] = {
          title: rule.visibleLabel,
          availableEntities: entitiesToShow,
          relatedType,
          direction: rule.direction,
          relationshipType: rule.relationshipType, // Store the relationship type from the rule
        };
      }
    });
    return relationshipSections;
  }, [
    entityForm.type,
    editingEntity?.type,
    editingEntity?.entity_type,
    entities,
    getRulesForEntityType,
    relationshipsReady,
  ]);

  const handleToggleAdvancedDetails = useCallback(() => {
    setShowAdvancedDetails((open) => {
      const next = !open;
      if (next) setAdvancedSectionsMounted(true);
      return next;
    });
  }, []);

  const handleEntityTypeChange = (newType) => {
    // Only clear subtype for new entities, preserve it for existing entities
    if (isCreateMode) {
      setEntityForm((prev) => ({
        ...prev,
        type: newType,
        subtype: "", // Clear subtype when entity type changes (new entities only)
        related_organizations: [],
        related_initiatives: [],
        related_business_capabilities: [],
        related_applications: [],
        related_it_components: [],
        related_business_actors: [],
        related_data_objects: [],
        related_business_contexts: [],
        related_providers: [],
        related_goals: [],
        related_business_processes: [],
        related_value_chains: [],
        provided_interfaces: [],
        consumed_interfaces: [],
      }));
    } else {
      // For existing entities, only change the type, preserve subtype
      setEntityForm((prev) => ({
        ...prev,
        type: newType,
      }));
    }
  };

  const handleSave = async () => {
    // Ensure we have the latest custom_attributes from the form state
    // This is a safeguard to ensure custom_attributes are always included
    const latestCustomAttributes = entityForm.custom_attributes || {};

    if (!entityForm.name?.trim()) {
      alert(embedded ? "Please enter a name" : "Please enter an entity name");
      return;
    }

    const formType = String(entityForm.type || "")
      .trim()
      .toLowerCase()
      .replace(/_/g, "-");
    if (formType === "interface") {
      const providers = Array.isArray(entityForm.interface_provided_by_app)
        ? entityForm.interface_provided_by_app.filter((e) => e?.id)
        : [];
      const consumers = Array.isArray(entityForm.interface_consumed_by_app)
        ? entityForm.interface_consumed_by_app.filter((e) => e?.id)
        : [];
      if (providers.length === 0 || consumers.length === 0) {
        if (isCreateMode) {
          setShowAdvancedDetails(true);
          setAdvancedSectionsMounted(true);
        }
        alert(
          "An interface needs at least one provider application and one consumer application before it can be saved."
        );
        return;
      }
    }

    const formSnapshot = JSON.parse(JSON.stringify(entityForm));
    setIsSaving(true);
    setSaveStatus("Preparing to save...");
    setSaveProgress(10);

    // Build payload outside try block so it's accessible in catch block for retry
    let savedEntity;
    // Normalize status field for backend compatibility
    // Extract custom_attributes and include them in payload
    // Use latestCustomAttributes as fallback to ensure we always have the most recent value
    const { custom_attributes, ...restForm } = formSnapshot;
    const customAttributesSource =
      custom_attributes || latestCustomAttributes || {};

    // Ensure custom_attributes is always an object (not undefined/null)
    // Handle both object and string formats
    let finalCustomAttributes = {};
    if (customAttributesSource) {
      if (
        typeof customAttributesSource === "object" &&
        !Array.isArray(customAttributesSource)
      ) {
        finalCustomAttributes = customAttributesSource;
      } else if (typeof customAttributesSource === "string") {
        try {
          finalCustomAttributes = JSON.parse(customAttributesSource);
          if (
            !finalCustomAttributes ||
            typeof finalCustomAttributes !== "object" ||
            Array.isArray(finalCustomAttributes)
          ) {
            finalCustomAttributes = {};
          }
        } catch (e) {
          console.warn("Failed to parse custom_attributes string:", e);
          finalCustomAttributes = {};
        }
      }
    }

    // Final safeguard: if finalCustomAttributes is still empty but latestCustomAttributes has data, use it
    if (
      Object.keys(finalCustomAttributes).length === 0 &&
      Object.keys(latestCustomAttributes).length > 0
    ) {
      console.warn(
        "⚠️ Using latestCustomAttributes as fallback - formSnapshot may be stale"
      );
      finalCustomAttributes = latestCustomAttributes;
    }

    const allowedPayloadKeys = [
      "name",
      "type",
      "description",
      "subtype",
      "alias_names",
      "parent",
      "children",
      "predecessors",
      "successors",
    ];

    const filteredBase = allowedPayloadKeys.reduce((acc, key) => {
      if (restForm[key] !== undefined) {
        acc[key] = restForm[key];
      }
      return acc;
    }, {});

    // Always include processing_status, defaulting to "Processed" if empty
    const statusValue =
      formSnapshot.status || formSnapshot.processing_status || "Processed";

    // Always include custom_attributes, even if empty, to ensure it's saved
    // Ensure it's always an object, never undefined or null
    // Final check: use the most recent value from entityForm if available
    const mostRecentCustomAttributes =
      entityForm.custom_attributes || finalCustomAttributes || {};
    const safeCustomAttributes =
      mostRecentCustomAttributes &&
      typeof mostRecentCustomAttributes === "object" &&
      !Array.isArray(mostRecentCustomAttributes)
        ? mostRecentCustomAttributes
        : finalCustomAttributes &&
            typeof finalCustomAttributes === "object" &&
            !Array.isArray(finalCustomAttributes)
          ? finalCustomAttributes
          : {};

    const payload = {
      ...filteredBase,
      processing_status: statusValue,
      custom_attributes: safeCustomAttributes, // Always include, always an object
    };

    if (
      String(formSnapshot.type || "")
        .trim()
        .toLowerCase()
        .replace(/_/g, "-") === "interface"
    ) {
      payload.provider_entity_ids = (
        formSnapshot.interface_provided_by_app || []
      )
        .map((e) => e?.id)
        .filter(Boolean);
      payload.consumer_entity_ids = (
        formSnapshot.interface_consumed_by_app || []
      )
        .map((e) => e?.id)
        .filter(Boolean);
    }

    try {
      setSaveStatus(embedded ? "Saving..." : "Saving entity...");
      setSaveProgress(30);

      if (isCreateMode) {
        savedEntity = await createEntity(payload);
      } else {
        const updateResponse = await updateEntity(entityId, payload);
        savedEntity = updateResponse?.entity || updateResponse;
      }

      setSaveStatus("Syncing relationships...");
      setSaveProgress(55);

      const removedRelationshipIds = await deleteRemovedRelationships();
      setSaveProgress(60);

      // Ensure we have the entity ID
      const entityId = savedEntity?.id || savedEntity?.entity?.id;
      if (entityId) {
        await createRelationships(entityId, removedRelationshipIds);
      }

      setSaveStatus("Finalizing...");
      setSaveProgress(90);

      setSaveProgress(100);
      // Full reload for parity checks (skipped for lightweight inline relationship saves)
      if (entityId && !skipPostSaveEntityReload) {
        try {
          const freshEntity = await api.getEntity(entityId, {
            bypassCache: true,
          });
          console.log(
            "🔍 Reloaded entity alias_names:",
            freshEntity?.alias_names
          );
          console.log(
            "🔍 Reloaded entity processing_status:",
            freshEntity?.processing_status
          );
          console.log("🔍 Reloaded entity status:", freshEntity?.status);
          console.log(
            "🔍 Reloaded entity custom_attributes:",
            freshEntity?.custom_attributes
          );
          console.log(
            "🔍 Reloaded entity custom_attributes type:",
            typeof freshEntity?.custom_attributes
          );
          console.log(
            "🔍 Reloaded entity custom_attributes keys:",
            freshEntity?.custom_attributes
              ? Object.keys(freshEntity.custom_attributes)
              : []
          );
          console.log(
            "🔍 Reloaded entity custom_attributes value:",
            JSON.stringify(freshEntity?.custom_attributes || {})
          );
          console.log(
            "🔍 Reloaded entity type:",
            freshEntity?.type || freshEntity?.entity_type
          );
          console.log("🔍 Reloaded entity parent:", freshEntity?.parent);
          console.log(
            "🔍 Reloaded entity children (raw):",
            freshEntity?.children,
            "type:",
            typeof freshEntity?.children
          );
          console.log(
            "🔍 Reloaded entity predecessors (raw):",
            freshEntity?.predecessors,
            "type:",
            typeof freshEntity?.predecessors
          );
          console.log(
            "🔍 Reloaded entity successors (raw):",
            freshEntity?.successors,
            "type:",
            typeof freshEntity?.successors
          );

          if (freshEntity) {
            savedEntity = freshEntity;
          }
        } catch (error) {
          console.error(
            "⚠️ Failed to reload entity, using saved response:",
            error
          );
          console.error("Error details:", error.message, error.stack);
        }
      } else {
        console.warn(
          "⚠️ Cannot reload entity - no entity ID found in savedEntity:",
          savedEntity
        );
      }

      const mismatches = collectStandardFieldMismatches(
        formSnapshot,
        savedEntity || {}
      );
      console.log("🔍 Status persistence check:", {
        formStatus: formSnapshot.status || formSnapshot.processing_status,
        savedProcessingStatus: savedEntity?.processing_status,
        savedStatus: savedEntity?.status,
        mismatches: mismatches.filter((m) => m.field === "status"),
      });
      const nonBlockingFields = [
        "status",
        "custom_attributes",
        "description",
        "alias_names",
      ];
      const blockingMismatches = mismatches.filter(
        (mismatch) => !nonBlockingFields.includes(mismatch.field)
      );

      if (blockingMismatches.length > 0) {
        console.error(
          "⚠️ Standard field persistence validation failed:",
          blockingMismatches
        );
        const mismatchFields = mismatches.map((m) => m.field).join(", ");
        const nameMismatch = mismatches.find((m) => m.field === "name");
        if (isCreateMode && nameMismatch) {
          alert(
            `Could not confirm the new item was saved as “${
              nameMismatch.expected || formSnapshot.name || "the name you entered"
            }”. ` +
              `The server returned “${
                nameMismatch.actual || savedEntity?.name || "a different item"
              }” instead ` +
              `(fields: ${mismatchFields}). ` +
              `Your form was left unchanged — try a slightly different name, or check whether this name was previously merged into another item.`
          );
          setSaveStatus("Create validation failed — form left unchanged.");
          setShowSuccess(false);
          return;
        }
        alert(
          `Standard field validation failed. The following fields did not match after saving: ${mismatchFields}. The form has been refreshed with the values returned by the server.`
        );
        setSaveStatus("Standard field validation failed.");
        // Parse hierarchical fields - they might come as JSON strings or arrays
        // Parse custom_attributes - handle both object and JSON string formats
        const parseCustomAttributes = (value) => {
          if (!value) return {};
          if (typeof value === "object" && !Array.isArray(value)) {
            return value;
          }
          if (typeof value === "string") {
            try {
              const parsed = JSON.parse(value);
              return typeof parsed === "object" && !Array.isArray(parsed)
                ? parsed
                : {};
            } catch {
              return {};
            }
          }
          return {};
        };

        setShowSuccess(false);
        setEntityForm((prev) => ({
          ...prev,
          name: savedEntity.name ?? prev.name ?? "",
          type: savedEntity.type || savedEntity.entity_type || prev.type,
          subtype: savedEntity.subtype || "",
          status:
            savedEntity.processing_status ||
            savedEntity.status ||
            prev.status ||
            "Processed",
          description: savedEntity.description || "",
          alias_names: savedEntity.alias_names || "",
          parent: savedEntity.parent || "",
          children: parseEntityIdArray(savedEntity.children),
          predecessors: parseEntityIdArray(savedEntity.predecessors),
          successors: parseEntityIdArray(savedEntity.successors),
          custom_attributes: parseCustomAttributes(
            savedEntity.custom_attributes
          ),
        }));
        return;
      }
      if (mismatches.length > 0) {
        console.warn("Non-blocking field mismatches detected:", mismatches);
      }

      // Update form state with saved entity data to reflect changes immediately
      const savedStatus =
        savedEntity.processing_status || savedEntity.status || "Processed";

      // Parse custom_attributes - handle both object and JSON string formats
      const parseCustomAttributes = (value) => {
        if (!value) return {};
        if (typeof value === "object" && !Array.isArray(value)) {
          return value;
        }
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return typeof parsed === "object" && !Array.isArray(parsed)
              ? parsed
              : {};
          } catch {
            return {};
          }
        }
        return {};
      };
      const savedCustomAttributes = parseCustomAttributes(
        savedEntity.custom_attributes
      );

      const savedParent = savedEntity.parent || "";
      const savedChildren = parseEntityIdArray(savedEntity.children);
      const savedPredecessors = parseEntityIdArray(savedEntity.predecessors);
      const savedSuccessors = parseEntityIdArray(savedEntity.successors);

      console.log("🔄 Updating form state with saved entity:", {
        savedProcessingStatus: savedEntity.processing_status,
        savedStatus: savedEntity.status,
        finalStatus: savedStatus,
        currentFormStatus: entityForm.status,
        currentFormProcessingStatus: entityForm.processing_status,
        savedCustomAttributesKeys: Object.keys(savedCustomAttributes),
        savedCustomAttributes: savedCustomAttributes,
        savedCustomAttributesType: typeof savedCustomAttributes,
        savedCustomAttributesRaw: savedEntity.custom_attributes,
        savedCustomAttributesRawType: typeof savedEntity.custom_attributes,
        currentCustomAttributesKeys: Object.keys(
          entityForm.custom_attributes || {}
        ),
        savedParent: savedParent,
        savedChildren: savedChildren,
        savedPredecessors: savedPredecessors,
        savedSuccessors: savedSuccessors,
        rawChildren: savedEntity.children,
        rawPredecessors: savedEntity.predecessors,
        rawSuccessors: savedEntity.successors,
        entityType: savedEntity.type || savedEntity.entity_type,
      });
      setEntityForm((prev) => {
        const savedType =
          savedEntity.type || savedEntity.entity_type || prev.type || "";
        return {
          ...prev,
          name: savedEntity.name ?? prev.name ?? "",
          description: savedEntity.description ?? prev.description ?? "",
          type: savedType || prev.type,
          status: savedStatus,
          processing_status: savedStatus, // Ensure both are updated
          subtype: savedEntity.subtype || prev.subtype || "",
          alias_names: savedEntity.alias_names || prev.alias_names || "",
          custom_attributes: savedCustomAttributes, // Always use saved value, parsed if needed
          parent: savedParent, // Always use saved value
          children: savedChildren, // Always use saved value, parsed if needed
          predecessors: savedPredecessors, // Always use saved value, parsed if needed
          successors: savedSuccessors, // Always use saved value, parsed if needed
        };
      });

      setShowSuccess(true);
      onSave(savedEntity);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (error) {
      console.error("Error saving entity:", error);

      // Check if this is an entity type change error requiring confirmation
      // Check multiple possible error locations for axios errors
      // The error structure from api.js is: error.detail = { code, message, detail: {...} }
      // So we need to check error.detail.detail for the actual error details
      const errorResponse = error.response?.data;
      let errorDetail = null;

      const candidateErrorNodes = [
        error?.detail,
        errorResponse,
        errorResponse?.detail,
        errorResponse?.detail?.detail,
      ].filter(Boolean);

      const extractTypeChangeDetail = (node, depth = 0) => {
        if (!node || depth > 4) return null;
        if (Array.isArray(node)) {
          for (const item of node) {
            const found = extractTypeChangeDetail(item, depth + 1);
            if (found) return found;
          }
          return null;
        }
        if (typeof node !== "object") return null;
        if (
          node.requires_confirmation === true ||
          (Array.isArray(node.invalid_relationships) &&
            node.invalid_relationships.length > 0)
        ) {
          return node;
        }
        const keysToTry = ["detail", "data", "error", "payload"];
        for (const key of keysToTry) {
          if (node[key]) {
            const found = extractTypeChangeDetail(node[key], depth + 1);
            if (found) return found;
          }
        }
        return null;
      };

      // Try to extract the nested detail structure
      if (error.detail && typeof error.detail === "object") {
        // If error.detail has a nested detail property, use that
        if (error.detail.detail && typeof error.detail.detail === "object") {
          errorDetail = error.detail.detail;
        } else {
          // Otherwise use error.detail directly
          errorDetail = error.detail;
        }
      } else if (errorResponse?.detail) {
        // If detail is an object with nested detail, extract it
        if (
          typeof errorResponse.detail === "object" &&
          errorResponse.detail.detail
        ) {
          errorDetail = errorResponse.detail.detail;
        } else if (typeof errorResponse.detail === "object") {
          errorDetail = errorResponse.detail;
        }
      }

      // Deep fallback: search common nested wrappers for the confirmation payload.
      if (!errorDetail || typeof errorDetail !== "object") {
        for (const node of candidateErrorNodes) {
          const found = extractTypeChangeDetail(node);
          if (found) {
            errorDetail = found;
            break;
          }
        }
      } else {
        const found = extractTypeChangeDetail(errorDetail);
        if (found) {
          errorDetail = found;
        }
      }

      const currentType =
        editingEntity?.type || editingEntity?.entity_type || entityForm?.type || "";
      const nextType = payload?.type || entityForm?.type || "";
      const didAttemptTypeChange =
        !isCreateMode &&
        !!nextType &&
        String(nextType).toLowerCase() !== String(currentType).toLowerCase();

      // Check if error message contains entity type change keywords as fallback
      const isEntityTypeChangeError =
        error.message &&
        error.message.includes("would make") &&
        error.message.includes("relationship") &&
        error.message.includes("invalid");
      const isLikelyTypeChangeValidationError =
        error?.status === 400 && didAttemptTypeChange;

      // Track whether backend explicitly requested confirmation.
      const hasInvalidRelationships =
        errorDetail &&
        typeof errorDetail === "object" &&
        errorDetail.invalid_relationships &&
        Array.isArray(errorDetail.invalid_relationships) &&
        errorDetail.invalid_relationships.length > 0;
      const requiresConfirmation =
        !!(
          errorDetail &&
          typeof errorDetail === "object" &&
          errorDetail.requires_confirmation
        );

      if (
        requiresConfirmation ||
        isEntityTypeChangeError ||
        isLikelyTypeChangeValidationError
      ) {
        // Show confirmation modal
        const detail = errorDetail || {};

        // Extract information from error message if detail is incomplete
        let oldType = detail.old_type || currentType || "current type";
        let newType = detail.new_type || nextType || "new type";
        let relationshipCount = detail.invalid_relationships?.length || 0;
        let relationshipsList = detail.relationships_summary || [];
        let invalidRelationships = detail.invalid_relationships || [];

        // Try to parse from error message if detail is missing
        if (error.message && isEntityTypeChangeError) {
          const messageMatch = error.message.match(
            /from '([^']+)' to '([^']+)'/
          );
          if (messageMatch) {
            oldType = messageMatch[1];
            newType = messageMatch[2];
          }
          const countMatch = error.message.match(/(\d+) relationship/);
          if (countMatch) {
            relationshipCount = parseInt(countMatch[1]);
          }
        }

        // If backend asks for confirmation, allow user to proceed even when the
        // relationship details array isn't included in the payload.
        // Also treat message-matched type-change errors as confirmable so we never
        // fall through to the generic modal whose Proceed only dismisses.
        if (
          relationshipCount > 0 ||
          requiresConfirmation ||
          isEntityTypeChangeError ||
          isLikelyTypeChangeValidationError
        ) {
          entityTypeChangeRetryRef.current = {
            payload,
            entityId: editingEntity?.id,
          };
          const confirmationMessage =
            relationshipCount > 0
              ? `Changing entity type from "${oldType}" to "${newType}" would make ${relationshipCount} relationship(s) invalid. Would you like to remove these invalid relationships and continue?`
              : `Changing entity type from "${oldType}" to "${newType}" may invalidate existing relationships. Would you like to remove incompatible relationships and continue?`;

          setConfirmationModal({
            open: true,
            message: confirmationMessage,
            detail: {
              ...detail,
              old_type: oldType,
              new_type: newType,
              invalid_relationships: invalidRelationships,
              relationships_summary: relationshipsList,
              requires_confirmation: true,
            },
            onConfirm: async () => {
              const ctx = entityTypeChangeRetryRef.current;
              if (!ctx?.entityId) {
                setConfirmationModal({
                  open: true,
                  message:
                    "Could not apply changes because the saved request context was missing. Close this dialog and use Save again.",
                  detail: null,
                  onConfirm: () => {
                    setConfirmationModal({
                      open: false,
                      message: "",
                      detail: null,
                      onConfirm: null,
                      onCancel: null,
                    });
                  },
                  onCancel: () => {
                    setConfirmationModal({
                      open: false,
                      message: "",
                      detail: null,
                      onConfirm: null,
                      onCancel: null,
                    });
                  },
                });
                return;
              }

              try {
                setIsSaving(true);
                setSaveStatus("Removing invalid relationships...");
                setSaveProgress(30);

                const payloadWithFlag = {
                  ...ctx.payload,
                  remove_invalid_relationships: true,
                };

                const updateResponse = await updateEntity(
                  ctx.entityId,
                  payloadWithFlag
                );
                let savedLocal = updateResponse?.entity || updateResponse;

                setSaveStatus("Syncing relationships...");
                setSaveProgress(55);
                const removedRelationshipIds = await deleteRemovedRelationships();
                setSaveProgress(60);

                const entityId =
                  savedLocal?.id || savedLocal?.entity?.id || ctx.entityId;
                if (entityId) {
                  await createRelationships(entityId, removedRelationshipIds);
                }

                setSaveProgress(100);
                setShowSuccess(true);

                if (entityId) {
                  try {
                    const freshEntity = await api.getEntity(entityId, {
                      bypassCache: true,
                    });
                    if (freshEntity) {
                      savedLocal = freshEntity;
                    }
                  } catch (reloadError) {
                    console.error("Failed to reload entity:", reloadError);
                  }
                }

                entityTypeChangeRetryRef.current = null;
                setConfirmationModal({
                  open: false,
                  message: "",
                  detail: null,
                  onConfirm: null,
                  onCancel: null,
                });

                onSave(savedLocal);
                setTimeout(() => setShowSuccess(false), 2000);
              } catch (retryError) {
                console.error("Error retrying entity update:", retryError);
                const errorMsg =
                  retryError.detail?.message ||
                  retryError.detail?.detail?.message ||
                  (typeof retryError.detail?.detail === "string"
                    ? retryError.detail.detail
                    : "") ||
                  retryError.message ||
                  "Unknown error";
                setConfirmationModal({
                  open: true,
                  message: `Failed to update entity: ${errorMsg}`,
                  detail: null,
                  onConfirm: () => {
                    entityTypeChangeRetryRef.current = null;
                    setConfirmationModal({
                      open: false,
                      message: "",
                      detail: null,
                      onConfirm: null,
                      onCancel: null,
                    });
                  },
                  onCancel: () => {
                    entityTypeChangeRetryRef.current = null;
                    setConfirmationModal({
                      open: false,
                      message: "",
                      detail: null,
                      onConfirm: null,
                      onCancel: null,
                    });
                  },
                });
              } finally {
                setIsSaving(false);
                setSaveStatus("");
                setSaveProgress(0);
              }
            },
            onCancel: () => {
              entityTypeChangeRetryRef.current = null;
              setConfirmationModal({
                open: false,
                message: "",
                detail: null,
                onConfirm: null,
                onCancel: null,
              });
              setIsSaving(false);
              setSaveStatus("");
              setSaveProgress(0);
            },
          });
          return; // Exit early - modal will handle the confirmation
        }
      }

      // Regular error handling - show error in modal
      const errorMsg =
        error.detail?.message ||
        error.detail?.detail?.message ||
        (typeof error.detail?.detail === "string" ? error.detail.detail : "") ||
        error.message ||
        "Unknown error";
      setConfirmationModal({
        open: true,
        message: `Failed to save entity: ${errorMsg}`,
        detail: null,
        onConfirm: () => {
          setConfirmationModal({
            open: false,
            message: "",
            detail: null,
            onConfirm: null,
            onCancel: null,
          });
        },
        onCancel: () => {
          setConfirmationModal({
            open: false,
            message: "",
            detail: null,
            onConfirm: null,
            onCancel: null,
          });
        },
      });
      return; // Exit early - modal will handle the close
    } finally {
      setIsSaving(false);
      setSaveStatus("");
      setSaveProgress(0);
    }
  };

  const createRelationships = async (
    savedEntityId,
    relationshipIdsToDelete = []
  ) => {
    const relationshipSections = [
      "related_organizations",
      "related_initiatives",
      "related_business_capabilities",
      "related_applications",
      "related_it_components",
      "related_business_actors",
      "related_data_objects",
      "interface_affected_by_initiative_reverse",
      "related_business_contexts",
      "related_providers",
      "related_goals",
      "related_business_processes",
      "related_value_chains",
      "provided_interfaces",
      "consumed_interfaces",
      "interface_transfers_data",
      "related_successors",
      "related_organization_parents",
      "related_initiative_parents",
      "related_businesscontext_parents",
      "related_businesscapability_parents",
      "related_applicationinterface_parents",
      // Provider outgoing relationships
      "provider_supports_initiative",
      // Organization outgoing relationships
      "organization_uses_application",
      "organization_owns_initiatives",
      "organization_owns_objective_reverse", // Which objectives are owned by this organization?
      "organization_related_business_contexts",
      // Platform outgoing relationships
      "platform_supports_objective",
      "platform_runs_application",
      // Objective outgoing relationships
      "objective_improves_business_capability",
    ];

    // Incoming relationship sections (entity is the target)
    const incomingRelationshipSections = [
      // Interface incoming relationships
      "interface_provided_by_app",
      "interface_consumed_by_app",
      "interface_affected_by_initiative",
      // Data object incoming relationships
      "data_object_transferred_by_interface",
      "data_object_affected_by_initiative",
      "data_object_crud_by_application",
      // Technology component incoming relationships
      "technology_component_used_by_interface", // Incoming: interface -> technology-component (interface implements/uses IT component)
      "technology_component_used_by_application",
      "technology_component_offered_by_provider",
      "it_provided_by_provider_reverse", // Registry key for "Which providers offer this technology component?"
      "technology_component_owned_by_organization",
      "technology_component_affected_by_initiative",
      // Platform incoming relationships
      "platform_implemented_by_it_components",
      // Application incoming relationships
      "organization_uses_application_reverse",
      "initiative_affects_application_reverse",
      "platform_runs_application_reverse",
      // Business context incoming relationships
      "initiative_affects_business_context_reverse",
      "application_supports_business_context_reverse",
      "business_capability_linked_to_business_context_reverse",
      "organization_owns_business_context_reverse",
      // Business capability incoming relationships
      "initiative_improves_business_capability_reverse",
      "objective_improves_business_capability_reverse",
      "application_supports_business_capability_reverse",
      "business_context_linked_to_business_capability_reverse",
      // Note: objective_improves_business_capability is outgoing (objective -> business capability)
      // so it should NOT be in incomingRelationshipSections
      // Platform incoming relationships
      "initiative_affects_platform_reverse",
      // Objective incoming relationships
      "objective_supported_by_initiative",
      "objective_supported_by_platform",
      "objective_owned_by_organization",
      // Initiative incoming relationships
      "organization_owns_initiative_reverse",
      "provider_supports_initiative_reverse",
    ];

    // Build batch create list for all sections
    const batchCreates = [];

    // Fallback to SectionCatalog when rules-based populatedSections lacks metadata
    const getSectionMeta = (section) => {
      const fromRules = populatedSections[section];
      if (fromRules) return fromRules;
      try {
        // Lazy import to avoid circular deps
        const { SECTION_CATALOG } = require("./relationships/SectionCatalog");
        const {
          normalizeEntityType,
        } = require("./relationships/SectionConfig");
        const defs =
          SECTION_CATALOG[normalizeEntityType(entityForm.type)] || [];
        const def = defs.find((d) => d.key === section);
        if (def) {
          return {
            title: def.label,
            relatedType:
              (def.relatedEntityTypes && def.relatedEntityTypes[0]) ||
              undefined,
            direction: def.direction === "incoming" ? "incoming" : "outgoing",
            relationshipType:
              (def.relationshipTypes && def.relationshipTypes[0]) || undefined,
          };
        }
      } catch {}
      return null;
    };

    const pushSectionCreates = (
      relatedEntities,
      section,
      isIncoming = false
    ) => {
      if (!Array.isArray(relatedEntities) || relatedEntities.length === 0)
        return;
      const sectionData = getSectionMeta(section);
      const relationshipType = sectionData?.relationshipType || "uses";
      const sectionCreates = [];
      for (const relatedEntity of relatedEntities) {
        sectionCreates.push({
          source_entity_id: isIncoming ? relatedEntity.id : savedEntityId,
          target_entity_id: isIncoming ? savedEntityId : relatedEntity.id,
          type: relationshipType,
          description: relatedEntity.description || "",
          active_from: relatedEntity.activeFrom || null,
          active_until: relatedEntity.activeUntil || null,
          status: relatedEntity.status
            ? normalizeRelationshipStatus(relatedEntity.status)
            : "Proposed",
          confidence: 0.9,
        });
      }
      batchCreates.push(...sectionCreates);
    };

    for (const section of relationshipSections) {
      pushSectionCreates(entityForm[section] || [], section, false);
    }

    for (const section of incomingRelationshipSections) {
      // On create, interface endpoints are persisted atomically with the entity.
      if (
        isCreateMode &&
        String(entityForm.type || "")
          .trim()
          .toLowerCase()
          .replace(/_/g, "-") === "interface" &&
        (section === "interface_provided_by_app" ||
          section === "interface_consumed_by_app")
      ) {
        continue;
      }
      pushSectionCreates(entityForm[section] || [], section, true);
    }

    if (batchCreates.length > 0 || (relationshipIdsToDelete || []).length > 0) {
      const payload = {
        create: batchCreates,
        delete: relationshipIdsToDelete || [],
      };
      try {
        await api.batchUpdateRelationships(payload);
      } catch (err) {
        const incomplete = err?.incompleteInterfaces || [];
        const isEndpointInvariant =
          err?.field === "interface_endpoints" ||
          /interface .* incomplete|must have at least one provider/i.test(
            String(err?.message || "")
          );
        if (
          isEndpointInvariant &&
          (relationshipIdsToDelete || []).length &&
          window.confirm(
            `${err?.message || "This change would leave an interface incomplete."}\n\n` +
              `Delete the incomplete interface(s) entirely? This cannot be undone.`
          )
        ) {
          await api.batchUpdateRelationships({
            ...payload,
            cascade_delete_incomplete_interfaces: true,
          });
        } else {
          throw err;
        }
      }
    }
  };

  // Collect relationship ids removed in the UI (deleted in same batch as creates).
  const deleteRemovedRelationships = async () => {
    if (isCreateMode) return [];

    // Map section key to related entity type for quick filtering
    const sectionToType = {
      related_organizations: "organization",
      related_initiatives: "initiative",
      related_business_capabilities: "business-capability",
      related_applications: "application",
      related_it_components: "technology-component",
      related_business_actors: "business-actor",
      related_data_objects: "data-object",
      related_business_contexts: "business-context",
      related_providers: "provider",
      related_goals: "objective",
      provided_interfaces: "interface",
      consumed_interfaces: "interface",
      interface_affected_by_initiative_reverse: "interface",
      interface_provided_by_app: "application",
      interface_consumed_by_app: "application",
      interface_affected_by_initiative: "initiative",
      data_object_transferred_by_interface: "interface",
      data_object_affected_by_initiative: "initiative",
      data_object_crud_by_application: "application",
      technology_component_used_by_interface: "interface",
      technology_component_used_by_application: "application",
      technology_component_offered_by_provider: "provider",
      it_provided_by_provider_reverse: "provider", // Registry key for "Which providers offer this technology component?"
      technology_component_owned_by_organization: "organization",
      technology_component_affected_by_initiative: "initiative",
      // Platform outgoing relationships
      platform_supports_objective: "objective",
      platform_runs_application: "application",
      // Application incoming relationships
      organization_uses_application_reverse: "organization",
      initiative_affects_application_reverse: "initiative",
      platform_runs_application_reverse: "platform",
      // Business context incoming relationships
      initiative_affects_business_context_reverse: "initiative",
      application_supports_business_context_reverse: "application",
      business_capability_linked_to_business_context_reverse:
        "business-capability",
      organization_owns_business_context_reverse: "organization",
      // Business capability incoming relationships
      initiative_improves_business_capability_reverse: "initiative",
      application_supports_business_capability_reverse: "application",
      business_context_linked_to_business_capability_reverse:
        "business-context",
      objective_improves_business_capability_reverse: "objective",
      // objective_improves_business_capability is outgoing, not incoming
      // Platform incoming relationships
      initiative_affects_platform_reverse: "initiative",
      objective_supported_by_initiative: "initiative",
      objective_supported_by_platform: "platform",
      objective_owned_by_organization: "organization",
      objective_improves_business_capability: "business-capability",
      organization_owns_objective_reverse: "objective", // Organization owns objectives (outgoing)
      organization_owns_initiative_reverse: "organization",
      provider_supports_initiative_reverse: "provider",
      // Organization outgoing relationships
      organization_uses_application: "application",
      organization_owns_initiatives: "initiative",
      organization_related_business_contexts: "business-context",
      // Platform incoming relationships
      platform_implemented_by_it_components: "technology-component",
    };

    // relationships prop contains existing DB relationships; delete those whose other entity id is in removed list
    const existing = Array.isArray(effectiveRelationships)
      ? effectiveRelationships
      : [];

    // Collect all relationships to delete across all sections
    const allToDelete = [];

    for (const [sectionKey, removedIds] of Object.entries(removedItems || {})) {
      if (!Array.isArray(removedIds) || removedIds.length === 0) continue;
      const relatedType = sectionToType[sectionKey];
      // Identify relationships to delete
      const toDelete = existing.filter((rel) => {
        const otherId =
          rel.source_entity_id === editingEntity.id
            ? rel.target_entity_id
            : rel.source_entity_id;
        // Filter by section expected type using entities list (best effort)
        const other = (entities || []).find((e) => e.id === otherId);
        const typeMatches = relatedType ? other?.type === relatedType : true;
        return typeMatches && removedIds.includes(otherId);
      });

      // Add to batch delete list
      allToDelete.push(...toDelete);
    }

    return allToDelete.map((rel) => rel.id).filter(Boolean);
  };

  // createRelationshipsForSection no longer needed with batch endpoint

  const modalIsTypeChangeConfirm =
    !!confirmationModal.detail &&
    (confirmationModal.detail.requires_confirmation === true ||
      (Array.isArray(confirmationModal.detail.invalid_relationships) &&
        confirmationModal.detail.invalid_relationships.length > 0));

  return (
    <>
      <div
        className={
          embedded
            ? "relative z-0 flex flex-col min-h-0 rounded-lg bg-gray-900 border border-slate-200"
            : "fixed inset-0 bg-gray-900 flex flex-col z-50"
        }
      >
        {!relationshipsOnly && (
        <div className="bg-gray-800 border-b border-gray-700 p-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold text-white">
              {isCreateMode
                ? embedded
                  ? "Add item"
                  : "Create New Entity"
                : `Edit ${editingEntity?.name || (embedded ? "item" : "Entity")}`}
            </h1>
            <div className="flex items-center space-x-4">
              {!isCreateMode && editingEntity ? (
                <>
                  <button
                    onClick={handleViewSource}
                    className="px-3 py-1 bg-gray-700 text-white rounded text-sm hover:bg-gray-600 flex items-center gap-2"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    View Source
                  </button>
                  {!readOnly && entityId ? (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setEnrichModalOpen(true)}
                        className="px-3 py-1 bg-indigo-600 text-white rounded text-sm hover:bg-indigo-500 flex items-center gap-2"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        Enrich from AI
                      </button>
                      {enrichReady && enrichPreview ? (
                        <button
                          type="button"
                          title="Review enrichment results"
                          onClick={() => setEnrichReviewOpen(true)}
                          className="relative p-2 rounded-md bg-slate-700 hover:bg-slate-600 text-amber-200 border border-slate-600"
                        >
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                            />
                          </svg>
                          <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-slate-800" />
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {(editingEntity.type === "application" ||
                    editingEntity.entity_type === "application" ||
                    editingEntity.type === "ApplicationComponent" ||
                    editingEntity.entity_type === "ApplicationComponent") && (
                    <GenerateDocumentButton
                      entityId={entityId}
                      entityName={editingEntity.name}
                    />
                  )}
                </>
              ) : null}
              <button
                onClick={onCancel}
                className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                Close
              </button>
            </div>
          </div>
        </div>
        )}

        <div
          className={
            embedded ? "flex-1 min-h-0 overflow-y-auto p-6" : "flex-1 overflow-y-auto p-6 pb-24"
          }
        >
          <div className="max-w-4xl mx-auto pb-4">
            {!relationshipsOnly && (
              <>
            <EntityBasicInfo
              entityForm={entityForm}
              setEntityForm={setEntityForm}
              editingEntity={editingEntity}
              availableSubtypes={availableSubtypes}
              loadingSubtypes={loadingSubtypes}
              readOnly={readOnly}
              entityTypesOptions={entityTypesOptions}
              onEntityTypeChange={handleEntityTypeChange}
              mapLanguage={embedded}
              showAdvancedFields={!isCreateMode || showAdvancedDetails}
              onToggleAdvanced={
                isCreateMode && !readOnly
                  ? handleToggleAdvancedDetails
                  : undefined
              }
            />

            {/* Custom Attributes Section */}
            {(!isCreateMode || advancedSectionsMounted) && (
            <div
              className={
                isCreateMode && !showAdvancedDetails ? "hidden" : undefined
              }
            >
            <EntityCustomAttributes
              entityForm={entityForm}
              setEntityForm={setEntityForm}
              editingEntity={editingEntity}
              readOnly={readOnly}
              entityType={entityForm.type}
            />
            </div>
            )}
              </>
            )}

            {relationshipsOnly && !readOnly && (
              <p className="text-sm text-gray-400 mb-4">
                {onlyRelationshipSectionKey
                  ? "Add or remove links in this section only, then Save. Cancel discards changes."
                  : "Edit links below, then Save. Cancel returns to read-only view without saving."}
              </p>
            )}

            {readOnly ? (
              <RelationshipViewer
                entityForm={entityForm}
                entities={entities}
                relationships={effectiveRelationships}
                editingEntity={editingEntity}
              />
            ) : !isCreateMode || advancedSectionsMounted ? (
              <div
                className={
                  isCreateMode && !showAdvancedDetails ? "hidden" : undefined
                }
              >
              {relationshipsReady ? (
                <EntityRelationships
                  editingEntity={editingEntity}
                  entityForm={entityForm}
                  setEntityForm={setEntityForm}
                  entities={entities}
                  allEntities={allEntities}
                  relationships={effectiveRelationships}
                  removedItems={removedItems}
                  setRemovedItems={setRemovedItems}
                  readOnly={readOnly}
                  populatedSections={populatedSections}
                  onEntityTypeChange={handleEntityTypeChange}
                  onlySectionKey={onlyRelationshipSectionKey || undefined}
                  compactMode={Boolean(onlyRelationshipSectionKey)}
                  relationshipsLoading={relationshipsLoading}
                />
              ) : (
                <div className="mt-4 rounded-lg border border-gray-700 bg-gray-800/80 px-4 py-4 text-sm text-gray-300 flex items-center gap-2">
                  <svg
                    className="animate-spin w-4 h-4 shrink-0 text-blue-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Loading connections…
                </div>
              )}
              </div>
            ) : null}
          </div>
        </div>

        <EntityFormActions
          embedded={embedded}
          isSaving={isSaving}
          saveStatus={saveStatus}
          saveProgress={saveProgress}
          showSuccess={showSuccess}
          onSave={handleSave}
          onCancel={onCancel}
          readOnly={readOnly}
        />
      </div>

      {enrichModalOpen && entityId && !readOnly && !relationshipsOnly ? (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEnrichModalOpen(false);
            }
          }}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 p-4 border-b border-gray-700 shrink-0">
              <h2 className="text-lg font-semibold text-white">
                Enrich from AI
              </h2>
              <button
                type="button"
                onClick={() => setEnrichModalOpen(false)}
                className="px-2 py-1 bg-gray-700 text-white text-sm rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 min-h-0">
              <EntityEnrichPanel
                variant="dialog"
                anchorEntityId={editingEntity.id}
                anchorName={editingEntity.name}
                anchorEntityType={
                  editingEntity.type || editingEntity.entity_type || ""
                }
                onEnrichmentQueued={handleEnrichmentQueued}
                readOnly={readOnly}
                mapLanguage={embedded}
              />
            </div>
          </div>
        </div>
      ) : null}

      <EnrichReviewDialog
        open={enrichReviewOpen}
        onClose={() => {
          setEnrichReviewOpen(false);
          setEnrichReady(false);
          setEnrichPreview(null);
        }}
        editingEntity={editingEntity}
        preview={enrichPreview}
        reviewJobId={enrichReviewJobId}
        onRelationshipsApplied={handleRelationshipsAppliedFromEnrich}
        onDescriptionApplied={handleDescriptionAppliedFromEnrich}
        onPreviewPatch={handleEnrichPreviewPatch}
      />

      {/* Confirmation Modal for Entity Type Changes */}
      {confirmationModal.open && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              // Close modal on backdrop click
            }
          }}
        >
          <div
            className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-white">
                {modalIsTypeChangeConfirm
                  ? "Confirm Entity Type Change"
                  : "Error"}
              </h2>
              <button
                type="button"
                disabled={modalIsTypeChangeConfirm && isSaving}
                onClick={() => {
                  if (modalIsTypeChangeConfirm && isSaving) return;
                  const fn =
                    confirmationModal.onCancel || confirmationModal.onConfirm;
                  if (fn) fn();
                }}
                className="text-gray-400 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              {modalIsTypeChangeConfirm && isSaving && (
                <p className="text-sm text-blue-300 mb-3 flex items-center gap-2">
                  <span className="inline-block h-2 w-2 rounded-full bg-blue-400 animate-pulse" />
                  Applying changes…
                </p>
              )}
              <p className="text-gray-300 mb-4">{confirmationModal.message}</p>

              {confirmationModal.detail &&
              confirmationModal.detail.invalid_relationships &&
              Array.isArray(confirmationModal.detail.invalid_relationships) &&
              confirmationModal.detail.invalid_relationships.length > 0 ? (
                <>
                  <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 max-h-64 overflow-y-auto">
                    <p className="text-sm font-medium text-gray-400 mb-2">
                      Invalid Relationships (
                      {confirmationModal.detail.invalid_relationships.length}):
                    </p>
                    <ul className="space-y-2">
                      {(() => {
                        // Build relationships list from summary or invalid_relationships array
                        let relsList = [];
                        if (
                          confirmationModal.detail.relationships_summary &&
                          Array.isArray(
                            confirmationModal.detail.relationships_summary
                          ) &&
                          confirmationModal.detail.relationships_summary
                            .length > 0
                        ) {
                          relsList =
                            confirmationModal.detail.relationships_summary;
                        } else if (
                          confirmationModal.detail.invalid_relationships &&
                          Array.isArray(
                            confirmationModal.detail.invalid_relationships
                          ) &&
                          confirmationModal.detail.invalid_relationships
                            .length > 0
                        ) {
                          relsList =
                            confirmationModal.detail.invalid_relationships.map(
                              (rel) => {
                                if (typeof rel === "string") {
                                  return rel;
                                }
                                return `${rel.relationship_type || rel.type || "Unknown"} → ${rel.other_entity_name || rel.name || "Unknown"} (${rel.other_entity_type || rel.type || "Unknown"})`;
                              }
                            );
                        }

                        if (relsList.length === 0) {
                          return (
                            <li className="text-sm text-gray-400 italic">
                              No relationship details available
                            </li>
                          );
                        }

                        return relsList.map((rel, index) => {
                          // Support both summary strings and detailed relationship objects
                          if (typeof rel === "string") {
                            return (
                              <li
                                key={index}
                                className="text-sm text-gray-300 flex items-start"
                              >
                                <span className="text-red-400 mr-2">•</span>
                                <span>{rel}</span>
                              </li>
                            );
                          }

                          const relType =
                            rel.relationship_type || rel.type || "Unknown";
                          const otherName =
                            rel.other_entity_name || rel.name || "Unknown";
                          const otherType =
                            rel.other_entity_type ||
                            rel.other_type ||
                            rel.target_entity_type ||
                            rel.source_entity_type ||
                            "Unknown";
                          const relId =
                            rel.relationship_id || rel.id || rel.rel_id || null;
                          const direction =
                            rel.direction ||
                            (rel.is_source === true
                              ? "outgoing"
                              : rel.is_source === false
                                ? "incoming"
                                : null);

                          return (
                            <li
                              key={index}
                              className="text-sm text-gray-300 flex items-start"
                            >
                              <span className="text-red-400 mr-2">•</span>
                              <span className="min-w-0">
                                <span>
                                  {relType} {"->"} {otherName} ({otherType})
                                </span>
                                {(relId || direction) && (
                                  <span className="block text-xs text-gray-500 mt-0.5">
                                    {relId ? `ID: ${relId}` : ""}
                                    {relId && direction ? " | " : ""}
                                    {direction ? `Direction: ${direction}` : ""}
                                  </span>
                                )}
                              </span>
                            </li>
                          );
                        });
                      })()}
                    </ul>
                  </div>

                  <div className="mt-4 p-3 bg-yellow-900/20 border border-yellow-700/50 rounded-lg">
                    <p className="text-sm text-yellow-200">
                      ⚠️ These relationships will be permanently removed if you
                      proceed.
                    </p>
                  </div>
                </>
              ) : null}
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-700">
              {modalIsTypeChangeConfirm ? (
                <>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isSaving) return;
                      if (confirmationModal.onCancel) {
                        confirmationModal.onCancel();
                      }
                    }}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (isSaving) return;
                      const run = confirmationModal.onConfirm;
                      if (run) {
                        void Promise.resolve(run()).catch((err) => {
                          console.error("Confirmation handler failed:", err);
                        });
                      }
                    }}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? "Working…" : "Proceed"}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={
                      confirmationModal.onCancel ||
                      (() =>
                        setConfirmationModal({
                          open: false,
                          message: "",
                          detail: null,
                          onConfirm: null,
                          onCancel: null,
                        }))
                    }
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={
                      confirmationModal.onConfirm ||
                      confirmationModal.onCancel ||
                      (() =>
                        setConfirmationModal({
                          open: false,
                          message: "",
                          detail: null,
                          onConfirm: null,
                          onCancel: null,
                        }))
                    }
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
                  >
                    Close
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {sourceModal.open && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60]">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-2xl max-h-[90vh] flex flex-col p-4">
            <div className="flex items-center justify-between mb-2 shrink-0">
              <h2 className="text-sm font-semibold text-white">
                Source Description
              </h2>
              <button
                onClick={() =>
                  setSourceModal({ open: false, loading: false, data: null })
                }
                className="px-2 py-1 bg-gray-700 text-white text-xs rounded hover:bg-gray-600"
              >
                Close
              </button>
            </div>
            {sourceModal.loading ? (
              <div className="text-sm text-gray-300">Loading source...</div>
            ) : (
              <div className="text-sm text-gray-300 space-y-3 overflow-y-auto">
                {sourceModal.data?.name && (
                  <div>
                    <span className="text-gray-500">Name:</span>{" "}
                    <span className="text-white">{sourceModal.data.name}</span>
                  </div>
                )}
                {sourceModal.data?.description && (
                  <div>
                    <span className="text-gray-500">Description:</span>{" "}
                    <span className="text-white">
                      {sourceModal.data.description}
                    </span>
                  </div>
                )}
                {sourceModal.data?.extraction_reason && (
                  <div>
                    <span className="text-gray-500 font-medium">
                      Why this entity was identified:
                    </span>
                    <div className="mt-1 p-2 bg-gray-900/80 border border-gray-600 rounded text-gray-200">
                      {sourceModal.data.extraction_reason}
                    </div>
                  </div>
                )}
                <div>
                  <span className="text-gray-500 font-medium">
                    {sourceModal.data?.extraction_reason
                      ? "Full source text:"
                      : "Source text:"}
                  </span>
                  <div className="max-h-72 overflow-auto bg-gray-900 border border-gray-700 rounded p-3 mt-1">
                    <pre className="whitespace-pre-wrap text-xs text-gray-200">
                      {sourceModal.data?.text ||
                        sourceModal.data?.content ||
                        "No full text available."}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EntityForm;
